import { Button, Card, ButtonGroup, ToggleButton } from "react-bootstrap";
import { useState, useEffect, useMemo } from "react";
import {
  INSCRIPCION_FINAL_ENDPOINT,
  INSCRIPCION_FINAL_PDF_ENDPOINT,
} from "../../../helpers/endpoints";
import { useSelector, useDispatch } from "react-redux";
import { setMessageAction } from "../../../actions/messageActions";

import InscripcionFinalesTable from "./InscripcionFinalesTable";
import InscripcionFinalesCard from "./InscripcionFinalesCard";
import swal from "sweetalert2";
import { currencyFormatter } from "../../../helpers/tablaConfig";
import {
  BsFillPlusCircleFill,
  BsFillFileEarmarkPdfFill,
  BsFillTrashFill,
} from "react-icons/bs";
import { axiosDownloadFile } from "../../../helpers/axiosDownloadFile";

import Loader from "../../../helpers/Loader";
import useAxios from "../../../hooks/useAxios";
import ToggleCardTable from "../../utils/ToggleCardTable";
import "../../../assets/css/custom.scss";

export default function InscripcionFinales() {
  const [mesasFienalesDisponibles, setMesasFienalesDisponibles] =
    useState(null);
  const [mesasFinalesInscripto, setMesasFinalesInscripto] = useState(null);
  const [viewMode, setViewMode] = useState("card"); // Estado para controlar la vista ('card' o 'table')
  const [searchTerm, setSearchTerm] = useState(""); // Estado para el término de búsqueda
  const carreraSect = useSelector((state) => state.carr.carreraSect);
  const dispatch = useDispatch();
  const { request } = useAxios();
  const [loading, setLoading] = useState(true); // inicia en true

  const mensajeSistema = (message, tipo) => {
    dispatch(
      setMessageAction({
        type: tipo,
        mensaje: message
          ? message
          : "En este momento no podemos procesar la solicitud. Intente más tarde",
      })
    );
  };

  const regIncripcion = (dato) => {
    return { ...dato, btnText: "", disabled: false };
  };
  const regEliminar = (dato) => {
    return { ...dato, btnText: "", disabled: false };
  };

  const filteredMesasDisponibles = useMemo(() => {
    if (!mesasFienalesDisponibles || mesasFienalesDisponibles.length === 0) {
      return [];
    }
    return mesasFienalesDisponibles.filter((mesa) => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      return (
        mesa.materia.toLowerCase().includes(lowerCaseSearchTerm) ||
        (mesa.profes &&
          String(mesa.profes).toLowerCase().includes(lowerCaseSearchTerm))
      );
    });
  }, [mesasFienalesDisponibles, searchTerm]);

  const filteredMesasInscripto = useMemo(() => {
    if (!mesasFinalesInscripto || mesasFinalesInscripto.length === 0) {
      return [];
    }
    return mesasFinalesInscripto.filter((mesa) => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      return (
        mesa.materia.toLowerCase().includes(lowerCaseSearchTerm) ||
        (mesa.profes &&
          String(mesa.profes).toLowerCase().includes(lowerCaseSearchTerm))
      );
    });
  }, [mesasFinalesInscripto, searchTerm]);

  const getMateriasIncripcion = async () => {
    try {
      setLoading(true); // muestra loader al iniciar la petición
      const resp = await request({
        url: INSCRIPCION_FINAL_ENDPOINT + "/" + carreraSect?.alumnoPrograma,
      });
      let incripto = resp.filter((d) => d.estado !== "DISPONIBLE");
      setMesasFinalesInscripto(incripto.map((d) => regEliminar(d)));

      let disponible = resp.filter((d) => d.estado === "DISPONIBLE");
      setMesasFienalesDisponibles(disponible.map((d) => regIncripcion(d)));
    } catch (error) {
      setMesasFinalesInscripto([]);
      setMesasFienalesDisponibles([]);
      mensajeSistema(error.message, "ERROR");
    } finally {
      setLoading(false); // oculta loader al finalizar
    }
  };

  const info = (importe) => {
    if (importe != 0) {
      swal.fire({
        title: `<b>¡Inscripción confirmada!</b>`,
        html: `En caso de corresponder pago, por favor aguardá 1 hora y luego ingresá a la sección PAGOS de tu Portal de Alumno para abonar <br/>  <b>¿Dudas o inconvenientes?</b> Escribinos a tesoreria@udemm.edu.ar<br/> Importe ${importe}.`,
        icon: "info",
        toast: true,
      });
    }
  };

  const inscripcion = ({ mesaSelected }) => {
    swal
      .fire({
        title: `Materia: ${mesaSelected.data.materia}`,
        icon: "question",
        confirmButtonText: `Si`,
        cancelButtonText: "No",
        showCancelButton: true,
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          let seleccion = {
            ...mesaSelected.data,
            btnText: "Procesando...",
            disabled: true,
          };

          setMesasFienalesDisponibles((prev) =>
            prev.map((item) => (item.id === seleccion.id ? seleccion : item))
          );

          try {
            const resp = await request({
              url:
                INSCRIPCION_FINAL_ENDPOINT + "/" + carreraSect.alumnoPrograma,
              method: "post",
              body: {
                id: seleccion.id,
                preactaId: seleccion.preactaId,
              },
            });
            setMesasFienalesDisponibles((prev) =>
              prev.filter((m) => m.id !== seleccion.id)
            );
            setMesasFinalesInscripto((prev) => [...prev, regEliminar(resp)]);
            const param = {
              value: resp.importe,
            };
            const importe = currencyFormatter(param);
            if (importe !== 0) {
              info(importe);
            }
            mensajeSistema(`Te has inscripto a ${resp.materia}`, "success");
          } catch (error) {
            setMesasFienalesDisponibles((prev) =>
              prev.map((item) =>
                item.id === seleccion.id ? regIncripcion(item) : item
              )
            );
            mensajeSistema(error.message, "ERROR");
          }
        }
      });
  };

  const deleteMateriasIncripcion = ({ mesaSelected }) => {
    swal
      .fire({
        title: `Materia: ${mesaSelected.data.materia}`,
        icon: "question",
        confirmButtonText: `Si`,
        cancelButtonText: "No",
        showCancelButton: true,
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          let seleccion = {
            ...mesaSelected.data,
            btnText: "Procesando...",
            disabled: true,
          };

          setMesasFinalesInscripto((prev) =>
            prev.map((item) => (item.id === seleccion.id ? seleccion : item))
          );

          try {
            const resp = await request({
              url:
                INSCRIPCION_FINAL_ENDPOINT + "/" + carreraSect.alumnoPrograma,
              method: "put",
              body: {
                id: seleccion.id,
                preactaId: seleccion.preactaId,
              },
            });
            setMesasFinalesInscripto((prev) =>
              prev.filter((m) => m.id !== seleccion.id)
            );
            setMesasFienalesDisponibles((prev) => [
              ...prev,
              regIncripcion(resp),
            ]);
            mensajeSistema(
              `Se eliminó la inscripción a ${resp.materia}`,
              "success"
            );
          } catch (error) {
            setMesasFinalesInscripto((prev) =>
              prev.map((item) =>
                item.id === seleccion.id ? regEliminar(item) : item
              )
            );
            mensajeSistema(error.message, "ERROR");
          }
        }
      });
  };

  useEffect(() => {
    if (carreraSect?.alumnoPrograma) {
      getMateriasIncripcion();
    }
  }, [carreraSect?.alumnoPrograma]);

  const pdf = () => {
    axiosDownloadFile(
      INSCRIPCION_FINAL_PDF_ENDPOINT + "/" + carreraSect?.alumnoPrograma,
      "fianlesInscriptos"
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Card className="shadow m-5 p-3">
        <h2 className="text-center mt-3 mb-3">Mesas disponibles</h2>
        <div className="d-flex justify-content-end mb-3">
          <ToggleCardTable viewMode={viewMode} setViewMode={setViewMode} />
        </div>

        {viewMode === "table" ? (
          <InscripcionFinalesTable
            data={filteredMesasDisponibles}
            accion={inscripcion}
            btnIcon={<BsFillPlusCircleFill />}
            btnVariant={"btn btn-success btn-sm"}
            btnTitle={"Inscribirse a final"}
            ocultar={true}
          />
        ) : (
          <InscripcionFinalesCard
            datos={filteredMesasDisponibles}
            accion={inscripcion}
            btnIcon={<BsFillPlusCircleFill />}
            btnVariant={"btn btn-success btn-sm"}
            btnTitle={"Inscribirse a final"}
          />
        )}
      </Card>

      <Card className="shadow m-5 p-3">
        <h2 className="text-center m-3 ">Mesas inscritas</h2>
        <div style={{ textAlign: "right" }}>
          <Button
            className="bt btn-info mb-2"
            style={{ textAlign: "left", fontWeight: "bold" }}
            onClick={() => pdf()}
            disabled={
              mesasFinalesInscripto?.length === 0 ||
              mesasFinalesInscripto === null
                ? true
                : false
            }
          >
            {<BsFillFileEarmarkPdfFill />} Comprobante inscripción{" "}
          </Button>
        </div>
        {viewMode === "table" ? (
          <InscripcionFinalesTable
            data={filteredMesasInscripto}
            accion={deleteMateriasIncripcion}
            btnIcon={<BsFillTrashFill />}
            btnVariant={"btn btn-danger btn-sm"}
            btnTitle={"Eliminar mesa de final"}
            ocultar={false}
          />
        ) : (
          <InscripcionFinalesCard
            datos={mesasFinalesInscripto}
            accion={deleteMateriasIncripcion}
            btnIcon={<BsFillTrashFill />}
            btnVariant={"btn btn-danger btn-sm"}
            btnTitle={"Eliminar mesa de final"}
          />
        )}
      </Card>
    </>
  );
}
